import React, { useState, useEffect } from "react";
import { useCranes, useCraneSession, useProfiles } from "../../../hooks";
import { getBilletYardOperators } from "../../../repository/BilletYardInventoryRepository";
import {
  BilletYardOperator,
  BilletYardOperatorsApiResponse,
} from "../../../models/authModels";

interface OperatorCraneNamesProps {
  selectedCraneId: number | null;
  selectedOperatorId: number | null;
}

export const OperatorCraneNames = ({
  selectedCraneId,
  selectedOperatorId,
}: OperatorCraneNamesProps) => {
  const { cranes } = useCranes();
  const { isReadOnly } = useProfiles();

  // Operator state
  const [operators, setOperators] = useState<BilletYardOperator[]>([]);
  const [currentOperator, setCurrentOperator] =
    useState<BilletYardOperator | null>(null);

  // Load operators and set current operator
  useEffect(() => {
    let isMounted = true;

    const loadOperators = async () => {
      try {
        const response =
          (await getBilletYardOperators()) as BilletYardOperatorsApiResponse;
        if (response.ok && response.data?.tables?.[0]?.rows && isMounted) {
          const operatorsData = response.data.tables[0].rows;
          setOperators(operatorsData);

          // Find current operator if selectedOperatorId exists
          if (selectedOperatorId) {
            const operator = operatorsData.find(
              (op) => op.OperatorId === selectedOperatorId,
            );
            setCurrentOperator(operator || null);
          }
        }
      } catch (error) {
        /* Error handled silently */
      }
    };

    loadOperators();

    return () => {
      isMounted = false;
    };
  }, [selectedOperatorId]);

  // Update current operator when selectedOperatorId changes
  useEffect(() => {
    if (selectedOperatorId && operators.length > 0) {
      const operator = operators.find(
        (op) => op.OperatorId === selectedOperatorId,
      );
      setCurrentOperator(operator || null);
    } else {
      setCurrentOperator(null);
    }
  }, [selectedOperatorId, operators]);

  // Show display for all users - even read-only users should see their status
  // if (isReadOnly) {
  //   return null;
  // }

  const getDisplayLabel = (): string => {
    // If no operator or crane selected, show appropriate message
    if (!selectedOperatorId && !selectedCraneId) {
      return isReadOnly
        ? "Read-Only User, No Selection"
        : "No Crane, No Operator";
    }

    const operatorName = currentOperator?.OperatorName || "No operator";
    const selectedCrane = cranes.find((c) => c.CraneID === selectedCraneId);
    const craneName = selectedCrane?.CraneName || "No crane";

    // Show crane and operator info even for read-only users if available
    return `${craneName} : ${operatorName}`;
  };

  const getTextColor = (): string => {
    return "white"; // White text for blue background
  };

  const styles = {
    container: {
      fontSize: "14px",
      color: "white",
      textAlign: "center" as const,
      padding: "0px",
      backgroundColor: "transparent",
      borderRadius: "0px",
      minWidth: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: "0px",
      marginBottom: "0px",
    },
    text: {
      color: getTextColor(),
      fontWeight: "500" as const,
    },
  };

  return (
    <div style={styles.container}>
      <span style={styles.text}>{getDisplayLabel()}</span>
    </div>
  );
};
